import { useEffect, useMemo, useState } from "react";
import { fetchCertificationCatalog } from "../lib/api";
import { Input } from "./ui/input";
import { Award, X, Check } from "lucide-react";

/**
 * Certification autocomplete combobox — Free-type + suggestions.
 * Onchange writes the raw string (name) to `onChange(value)` so backend threshold stays a string.
 */
export default function CertificationCombobox({ value, onChange, testid }) {
  const [q, setQ] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    fetchCertificationCatalog().then((d) => setItems(d.items || [])).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    const ql = (q || "").toLowerCase().trim();
    if (!ql) return items.slice(0, 8);
    return items
      .filter((i) => i.name.toLowerCase().includes(ql) || i.code.toLowerCase().includes(ql) || i.domain.toLowerCase().includes(ql))
      .slice(0, 8);
  }, [q, items]);

  return (
    <div className="relative" data-testid={testid || "cert-combobox"}>
      <div className="flex items-center gap-1.5">
        <Award className="w-3.5 h-3.5 text-amber-600 shrink-0" />
        <Input
          value={q}
          placeholder="Sertifika adı yaz veya seç"
          className="h-8 text-sm"
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={(e) => { setQ(e.target.value); onChange?.(e.target.value); setOpen(true); }}
        />
        {q && (
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600"
            onMouseDown={(e) => { e.preventDefault(); setQ(""); onChange?.(""); }}
            title="Temizle"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 max-h-64 overflow-auto bg-white border border-slate-200 rounded-lg shadow-lg" data-testid="cert-combobox-list">
          {filtered.map((c) => (
            <button
              key={c.code}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b border-slate-50 last:border-0 flex items-center justify-between gap-2 group"
              onMouseDown={(e) => { e.preventDefault(); setQ(c.name); onChange?.(c.name); setOpen(false); }}
              data-testid={`cert-suggest-${c.code}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{c.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{c.domain} · {c.issuer}</div>
              </div>
              <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded shrink-0 group-hover:bg-amber-100 group-hover:text-amber-800">{c.code}</span>
            </button>
          ))}
          {q && !filtered.some((c) => c.name.toLowerCase() === q.toLowerCase()) && (
            <button
              type="button"
              className="w-full text-left px-3 py-2 bg-slate-50 hover:bg-emerald-50 flex items-center gap-2 text-sm text-emerald-800"
              onMouseDown={(e) => { e.preventDefault(); onChange?.(q); setOpen(false); }}
            >
              <Check className="w-3.5 h-3.5" /> &ldquo;{q}&rdquo; olarak serbest kaydet
            </button>
          )}
        </div>
      )}
    </div>
  );
}
